"use client";

import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import type { SuccessData } from "../PortalManager";

/**
 * Inline styles for the success portal (must work on any host page)
 */
const styles = {
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  container: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "32px",
    maxWidth: "420px",
    width: "100%",
    margin: "16px",
    textAlign: "center" as const,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  iconContainer: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    backgroundColor: "#dcfce7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 24px",
  },
  checkIcon: {
    width: "36px",
    height: "36px",
    color: "#16a34a",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 8px 0",
  },
  subtitle: {
    fontSize: "16px",
    color: "#6b7280",
    margin: "0 0 24px 0",
    lineHeight: 1.5,
  },
  detailsCard: {
    backgroundColor: "#f9fafb",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "24px",
    textAlign: "left" as const,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
  },
  detailLabel: {
    fontSize: "14px",
    color: "#6b7280",
    margin: 0,
  },
  detailValue: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#111827",
    margin: 0,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0 0 0",
    borderTop: "1px solid #e5e7eb",
    marginTop: "8px",
  },
  totalLabel: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
  },
  totalValue: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#111827",
    margin: 0,
  },
  emailNote: {
    fontSize: "14px",
    color: "#6b7280",
    marginBottom: "24px",
    lineHeight: 1.5,
  },
  closeButton: (primaryColor: string) => ({
    width: "100%",
    padding: "14px 24px",
    backgroundColor: primaryColor,
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "opacity 0.2s",
  }),
};

// Check mark SVG icon
function CheckIcon() {
  return (
    <svg
      style={styles.checkIcon}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export type SuccessScreenPortalProps = {
  isOpen: boolean;
  successData: SuccessData;
  primaryColor: string;
  onClose: () => void;
};

export function SuccessScreenPortal({
  isOpen,
  successData,
  primaryColor,
  onClose,
}: SuccessScreenPortalProps) {
  const formatPrice = useCallback((amount: number, currency: string) => {
    const symbols: Record<string, string> = { GBP: "£", USD: "$", EUR: "€" };
    const symbol = symbols[currency] || currency;
    return `${symbol}${(amount / 100).toFixed(2)}`;
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  // Auto-close after 5 seconds
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const portalContent = (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.container}>
        <div style={styles.iconContainer}>
          <CheckIcon />
        </div>

        <h2 style={styles.title}>Payment Successful!</h2>
        <p style={styles.subtitle}>
          Your booking has been confirmed.
        </p>

        {(successData.courseName || successData.total) && (
          <div style={styles.detailsCard}>
            {successData.courseName && (
              <div style={styles.detailRow}>
                <p style={styles.detailLabel}>Booking</p>
                <p style={styles.detailValue}>{successData.courseName}</p>
              </div>
            )}
            {successData.total !== undefined && successData.total > 0 && (
              <div style={styles.totalRow}>
                <p style={styles.totalLabel}>Total Paid</p>
                <p style={styles.totalValue}>
                  {formatPrice(successData.total, successData.currency || "GBP")}
                </p>
              </div>
            )}
          </div>
        )}

        {successData.email && (
          <p style={styles.emailNote}>
            A confirmation email has been sent to <strong>{successData.email}</strong>
          </p>
        )}

        <button
          style={styles.closeButton(primaryColor)}
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </div>
  );

  // Render via portal to document.body (outside Shadow DOM)
  return typeof document !== "undefined"
    ? createPortal(portalContent, document.body)
    : null;
}

